import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAppointmentById,
  postCreateAppointment,
  putUpdateAppointment,
} from 'services/scheduleServices';
import { useLanguage } from '../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../utils/createScopedI18n';
import { scheduleI18n } from '../i18n';

const ModalCreateEditEvent = ({
  DesignSystemComponents,
  show,
  onClose,
  onCreated,
  appointmentId,
}) => {
  const { Modal, Input } = DesignSystemComponents;

  const { language } = useLanguage();
  const scheduleLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: scheduleI18n });

  useEffect(() => {
    scheduleI18n.changeLanguage(scheduleLanguage);
  }, [scheduleLanguage]);

  const isEditMode = !!appointmentId;

  const getTodayString = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getInitialFormState = () => ({
    title: '',
    description: '',
    startDate: getTodayString(),
    startTime: '',
    endDate: getTodayString(),
    endTime: '',
  });

  const [creating, setCreating] = useState(false);
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');
  const [appointmentForm, setAppointmentForm] = useState(getInitialFormState());

  const handleAppointmentChange = (name, value) => {
    setAppointmentForm((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === 'endDate' && dateError) {
        setDateError('');
      }

      if (name === 'endTime' && timeError) {
        setTimeError('');
      }

      if (name === 'startDate') {
        if (dateError) setDateError('');
        if (timeError) setTimeError('');
      }

      if (name === 'startTime' && timeError) {
        setTimeError('');
      }

      return updated;
    });
  };

  const buildUtcDateTime = (date, time) => {
    const local = new Date(`${date}T${time}`);
    return local.toISOString();
  };

  const hasRequiredFields =
    appointmentForm.title.trim() &&
    appointmentForm.startDate &&
    appointmentForm.startTime &&
    appointmentForm.endDate &&
    appointmentForm.endTime;

  const hasValidationErrors = !!dateError || !!timeError;

  const isFormValid = hasRequiredFields && !hasValidationErrors;

  const handleClose = () => {
    setAppointmentForm(getInitialFormState());
    setDateError('');
    setTimeError('');
    onClose();
  };

  const validateForm = () => {
    const { startDate, startTime, endDate, endTime } = appointmentForm;

    setDateError('');
    setTimeError('');

    if (!startDate || !endDate || !startTime || !endTime) {
      return false;
    }

    if (endDate < startDate) {
      setDateError(t('error_end_date'));
      return false;
    }

    if (endDate === startDate && endTime <= startTime) {
      setTimeError(t('error_end_time'));
      return false;
    }

    return true;
  };

  const handleSaveAppointment = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    try {
      setCreating(true);

      const startUtc = buildUtcDateTime(
        appointmentForm.startDate,
        appointmentForm.startTime
      );

      const endUtc = buildUtcDateTime(
        appointmentForm.endDate,
        appointmentForm.endTime
      );

      const payload = {
        meetingTypeId: 1,
        title: appointmentForm.title,
        description: appointmentForm.description || null,
        startAtUtc: startUtc,
        endAtUtc: endUtc,
        location: null,
        attendees: null,
        appointmentStatusId: 1,
      };

      if (isEditMode) {
        await putUpdateAppointment.putUpdate(appointmentId, payload);
      } else {
        await postCreateAppointment.create(payload);
      }

      onCreated();
      onClose();
    } catch (error) {
      console.warn('Error guardando evento:', error);
    } finally {
      setCreating(false);
    }
  };

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatLocalTime = (date) => {
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${min}`;
  };

  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) return;

      try {
        const data = await getAppointmentById.getAppointment(appointmentId);

        const start = new Date(data.startAtUtc + 'Z');
        const end = new Date(data.endAtUtc + 'Z');

        setAppointmentForm({
          title: data.title || '',
          description: data.description || '',
          startDate: formatLocalDate(start),
          startTime: formatLocalTime(start),
          endDate: formatLocalDate(end),
          endTime: formatLocalTime(end),
        });
      } catch (error) {
        console.warn('Error cargando evento:', error);
      }
    };

    if (show && appointmentId) {
      loadAppointment();
    }

    if (show && !appointmentId) {
      setAppointmentForm(getInitialFormState());
    }
  }, [show, appointmentId]);

  useEffect(() => {
    if (show) {
      setAppointmentForm(getInitialFormState());
      setDateError('');
      setTimeError('');
    }
  }, [show]);

  return (
    <Modal
      title={isEditMode ? t('modal_title_edit') : t('modal_title_create')}
      show={show}
      onClose={handleClose}
      secondaryBtnTxt={t('modal_cancel')}
      secondaryBtnAction={handleClose}
      primaryBtnTxt={t('modal_save')}
      primaryBtnAction={handleSaveAppointment}
      disabledPrimaryBtn={creating || !isFormValid}
      width='640px'
    >
      <div className='row'>
        <div className='col-12'>
          <Input
            label={t('field_title_label')}
            placeholder={t('field_title_placeholder')}
            value={appointmentForm.title}
            onChange={(e) => handleAppointmentChange('title', e.target.value)}
            required
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-6'>
          <Input
            type='date'
            label={t('field_start_date')}
            value={appointmentForm.startDate}
            min={getTodayString()}
            onChange={(e) =>
              handleAppointmentChange('startDate', e.target.value)
            }
            required
          />
        </div>
        <div className='col-6'>
          <Input
            type='time'
            label={t('field_start_time')}
            value={appointmentForm.startTime}
            onChange={(e) =>
              handleAppointmentChange('startTime', e.target.value)
            }
            required
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-6'>
          <Input
            type='date'
            label={t('field_end_date')}
            value={appointmentForm.endDate}
            min={getTodayString()}
            onChange={(e) => handleAppointmentChange('endDate', e.target.value)}
            error={dateError}
            required
          />
        </div>
        <div className='col-6'>
          <Input
            type='time'
            label={t('field_end_time')}
            value={appointmentForm.endTime}
            onChange={(e) => handleAppointmentChange('endTime', e.target.value)}
            error={timeError}
            required
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-12'>
          <label className='form-label mb-2'>{t('field_description_label')}</label>
          <textarea
            className='form-control'
            placeholder={t('field_description_placeholder')}
            rows={5}
            value={appointmentForm.description}
            onChange={(e) =>
              handleAppointmentChange('description', e.target.value)
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalCreateEditEvent;
